/**
 * Form12_PolicyPatientResponsibilityForFees.jsx
 * Collection: patient_responsibility_fees_v2
 * Redirect: /forms/13-remote-monitoring
 *
 * Shorter version of fees policy (no practice phone fields on the form).
 */
import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { FormShell, Field, SigBlock, saveToMongo, downloadPDF, useToast, C } from "../shared/formKit";

const API_URL = "/api/forms/patient-responsibility-fees-v2";
const NEXT    = "/forms/13-remote-monitoring";

export default function PolicyPatientResponsibilityForFees({
  patientName="", practiceName="", mainPhone="", otherPhone="", onSubmit,
}) {
  const history=useHistory();
  const formRef=useRef(null);
  const sigRef=useRef(null);
  const [sigEmpty,setSigEmpty]=useState(true);
  const [submitting,setSubmitting]=useState(false);
  const [savedId,setSavedId]=useState(null);
  const [showToast,Toast]=useToast();

  const [form,setForm]=useState({
    responsibleParty:patientName, relationship:"Self",
    sigDate:new Date().toISOString().split("T")[0],
  });
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const getSig=()=>(!sigRef.current||sigRef.current.isEmpty())?null:sigRef.current.toDataURL("image/png");

  const buildDoc=()=>({
    formType:"patient_responsibility_fees_v2",
    patient:{name:patientName},
    practiceName, mainPhone, otherPhone,
    authorization:{responsibleParty:form.responsibleParty,relationship:form.relationship,sigDate:form.sigDate||null,signatureDataURL:getSig()},
    meta:{submittedAt:new Date().toISOString(),formVersion:"1.0.0"},
  });

  const handleSubmit=async()=>{
    if(sigEmpty){showToast("Please sign before submitting.","error");return;}
    setSubmitting(true);
    try{
      const doc=buildDoc();
      const res=typeof onSubmit==="function"?await onSubmit(doc):await saveToMongo(doc,API_URL);
      if(res?._id)setSavedId(res._id);
      showToast("Saved! Redirecting…","success");
      setTimeout(()=>history.push(NEXT),1800);
    }catch(e){console.error(e);showToast("Save failed.","error");}
    finally{setSubmitting(false);}
  };

  const handleDownload=async()=>{
    try{await downloadPDF(formRef,`fees-policy-v2-${patientName||"patient"}.pdf`);showToast("Downloaded!");}
    catch{showToast("PDF failed.","error");}
  };

  return (
    <FormShell title="Policy on Patient Responsibility for Fees"
      subtitle={`Thank you for choosing ${practiceName||"our practice"} for your care.`}
      dbLabel="MongoDB · patient_responsibility_fees_v2"
      patientName={patientName} savedId={savedId} formRef={formRef}
      onSubmit={handleSubmit} onDownload={handleDownload} submitting={submitting}>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">📋</span>Policy</div>
        <div className="wf-policy">
          <p>We believe that good care for you and your family starts with good communication. We have created this policy to help our patients understand the responsibilities they and their families have for payment of our fees. If you have questions about fees or payment, please contact our billing department at <strong>{mainPhone}</strong> or <strong>{otherPhone}</strong>.</p>
          <p>We require that our patients promptly pay all charges presented to them. If reimbursed directly by a program for the cost of your care, you must still pay our charges promptly.</p>
          <p>If you do not agree with patient responsibility amounts set by your insurance or government program, this is a matter between you and that program. We still require you to promptly pay the entire charge we present to you.</p>
          <p>Payment is due at the time services are provided. We may also present charges by written statement or telephone calls following a visit. We expect payment in full the first time a charge is presented.</p>
          <p><strong>Note:</strong> All payments outstanding after <strong>90 days</strong> will be automatically referred to our collection agency. By signing below, you acknowledge and accept financial responsibility for services provided.</p>
        </div>
      </div>

      <SigBlock sigRef={sigRef} sigEmpty={sigEmpty} setSigEmpty={setSigEmpty}
        responsibleParty={form.responsibleParty} setResponsibleParty={v=>set("responsibleParty",v)}
        relationship={form.relationship} setRelationship={v=>set("relationship",v)}
        sigDate={form.sigDate} setSigDate={v=>set("sigDate",v)} />

      {Toast}
    </FormShell>
  );
}
/*
── Mongoose Schema ────────────────────────────────────────────────────────────
const FeesV2Schema = new Schema({
  formType:{type:String,default:"patient_responsibility_fees_v2"},
  patient:{name:String}, practiceName:String, mainPhone:String, otherPhone:String,
  authorization:{responsibleParty:String,relationship:String,sigDate:Date,signatureDataURL:String},
  meta:{submittedAt:Date,formVersion:String},
},{timestamps:true,collection:"patient_responsibility_fees_v2"});
*/
